/**
 * @fileoverview Primary navigation component that handles both desktop and mobile navigation
 * @module features/primaryNavigation/primaryNavigationNew
 */
import React, { FunctionComponent, useState } from 'react';
import { PrimaryNavigation, ResponsiveMenu } from 'basecamp';
import { useConnectPrimaryNavLinks, useSummitPrimaryNavLinks } from '../../routes/hooks';
import { useSelector } from 'react-redux';
import { getConfig, getFeature } from 'features/app/redux/features';
import { censusStatus } from 'features/app/redux/context';
import moment from 'moment';
import { allowCensusStatusModalTracker } from '../../utils/censusHelpers';
/**
 * @typedef {Object} Props Empty props object as component doesn't accept any props
 */
type Props = {
	isGroup: boolean;
};

/**
 * Primary navigation component that displays navigation links and handles responsive behavior
 * @component
 * @param {Props} props - Component props (empty object)
 * @returns {JSX.Element} The rendered navigation component with responsive menu
 */
const AppPrimaryNavigation: FunctionComponent<Props> = ({ isGroup }) => {
	/**
	 * State to manage the small screen menu
	 */
	const [smallScreenMenuIsOpen, setSmallScreenMenuIsOpen] = useState<boolean>(false);

	/**
	 * Get menu configurations for different screen sizes
	 * @type {{largeScreenMenu: JSX.Element[], smallScreenMenu: JSX.Element[]}}
	 */
	const connectNavigation = useConnectPrimaryNavLinks();
	const summitNavigation = useSummitPrimaryNavLinks();

	//Fetch the Census Window
	const censusWindow = useSelector(getConfig('census_window'));
	const censusWindowFlag = useSelector(getFeature('census_window'));

	/**
	 * Memoized values for large and small screen menus based on whether viewing Summit or Connect
	 * @type {{largeScreenMenu: JSX.Element[], smallScreenMenu: JSX.Element[]}}
	 */
	const largeScreenMenu = React.useMemo(
		() => (isGroup ? summitNavigation.largeScreenMenu : connectNavigation.largeScreenMenu),
		[isGroup, summitNavigation.largeScreenMenu, connectNavigation.largeScreenMenu]
	);

	const smallScreenMenu = React.useMemo(
		() => (isGroup ? summitNavigation.smallScreenMenu : connectNavigation.smallScreenMenu),
		[isGroup, summitNavigation.smallScreenMenu, connectNavigation.smallScreenMenu]
	);

	const statusOfCensus = useSelector(censusStatus);

	const dateTimeUtc = moment.utc();
	// Check if current date is within census window
	const isWithinCensusWindow =
		censusWindowFlag &&
		dateTimeUtc.isBetween(moment.utc(censusWindow.start), moment.utc(censusWindow.end));

	const disableMenuButtonsForCensus =
		isWithinCensusWindow &&
		statusOfCensus !== undefined &&
		allowCensusStatusModalTracker(statusOfCensus);

	return (
		!disableMenuButtonsForCensus && (
			<PrimaryNavigation width={1} zIndex={9001}>
				<ResponsiveMenu
					largeScreenMenu={largeScreenMenu}
					smallScreenMenu={smallScreenMenu}
					setSmallScreenMenuIsOpen={(isOpen: boolean) => setSmallScreenMenuIsOpen(isOpen)}
					smallScreenMenuIsOpen={smallScreenMenuIsOpen}
				/>
			</PrimaryNavigation>
		)
	);
};

/**
 * Memoized version of PrimaryNav component to prevent unnecessary re-renders
 */
export default AppPrimaryNavigation;
